import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Pencil, Code2, X, Plus, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SuggestedSkill {
  id: string;
  name: string;
}

interface SkillsSectionProps {
  skills: string[];
  suggestedSkills: SuggestedSkill[];
  isLoadingSkills: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onEditClick: () => void;
  onCancel: () => void;
  onSave: () => void;
  onAddSkill: (skill: string) => void;
  onRemoveSkill: (skill: string) => void;
  onResetSkills?: () => void;
}

export function SkillsSection({
  skills,
  suggestedSkills,
  isLoadingSkills,
  isEditing,
  isSaving,
  onEditClick,
  onSave,
  onAddSkill,
  onRemoveSkill,
  onResetSkills,
}: SkillsSectionProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [customSkill, setCustomSkill] = useState('');
  const [inputError, setInputError] = useState('');

  const hasSkills = skills.length > 0;
  const showEditing = isEditing || !hasSkills;

  const handleAddCustomSkill = () => {
    const trimmedSkill = customSkill.trim();

    if (!trimmedSkill) {
      setInputError('Please enter a skill');
      return;
    }

    if (skills.some(s => s.toLowerCase() === trimmedSkill.toLowerCase())) {
      setInputError('This skill is already added');
      return;
    }

    if (skills.length >= 10) {
      setInputError('Maximum 10 skills allowed');
      return;
    }

    onAddSkill(trimmedSkill);
    setCustomSkill('');
    setInputError('');
    setIsAddDialogOpen(false);
  };

  const handleDialogClose = () => {
    setIsAddDialogOpen(false);
    setCustomSkill('');
    setInputError('');
  };

  return (
    <div className={cn(
      "rounded-2xl sm:rounded-3xl lg:rounded-[40px] p-5 sm:p-6 lg:p-8",
      !hasSkills
        ? "bg-amber-50/50 border-2 border-dashed border-amber-500/30"
        : "bg-white border border-slate-200"
    )}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Code2 className="h-5 w-5 sm:h-6 sm:w-6 text-slate-700" />
          <h3 className="text-xl sm:text-2xl text-slate-900">Skills & Expertise</h3>
          {!hasSkills && (
            <Badge className="bg-amber-500 text-white rounded-[48px] px-3 sm:px-4 py-1 text-xs">
              Incomplete
            </Badge>
          )}
        </div>
        {hasSkills && !isEditing && (
          <button
            onClick={onEditClick}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-[48px] border-2 border-slate-200 bg-white text-slate-900 font-normal text-sm sm:text-base hover:bg-slate-50 transition-colors"
          >
            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
            Edit
          </button>
        )}
      </div>

      {/* Empty State - No skills yet */}
      {!hasSkills && !isEditing ? (
        <div className="flex flex-col items-center py-12">
          {/* Icon Circle */}
          <div className="w-20 h-20 rounded-[40px] bg-slate-200 flex items-center justify-center mb-6">
            <Code2 className="w-6 h-6 text-slate-500" />
          </div>

          {/* Title */}
          <h3 className="text-xl font-normal text-slate-900 text-center mb-2">
            Add Your Skills
          </h3>

          {/* Description */}
          <p className="text-sm text-slate-500 text-center max-w-[280px] leading-relaxed mb-6">
            Help clients find you by adding your AI/ML skills, frameworks, and areas of expertise.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="flex items-center gap-2 px-6 py-3 rounded-[48px] text-white font-normal text-base"
            style={{
              background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
            }}
          >
            <Plus className="w-5 h-5" />
            Add Skill
          </button>
        </div>
      ) : showEditing ? (
        <div className="space-y-6">
          {/* Add Skill Button */}
          <div>
            <Button
              onClick={() => setIsAddDialogOpen(true)}
              disabled={skills.length >= 10}
              className="rounded-[48px] text-white font-normal"
              style={{
                background: skills.length >= 10
                  ? undefined
                  : 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Skill
            </Button>
          </div>

          {/* Selected Skills */}
          <div>
            <Label className="text-sm font-bold text-slate-700 mb-3 block">
              Your Skills ({skills.length}/10)
            </Label>
            <div className="flex flex-wrap gap-2 min-h-[40px]">
              {skills.length > 0 ? skills.map((skill) => (
                <Badge
                  key={skill}
                  className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveSkill(skill);
                    }}
                    className="ml-1 hover:text-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )) : (
                <span className="text-slate-400 text-sm">No skills selected yet</span>
              )}
            </div>
          </div>

          {/* Suggested Skills */}
          <div>
            <Label className="text-sm font-bold text-slate-700 mb-3 block">Suggested Skills (click to add)</Label>
            {isLoadingSkills ? (
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-8 w-24 rounded-full" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {suggestedSkills
                  .filter(skill => !skills.some(s => s.toLowerCase() === skill.name.toLowerCase()))
                  .slice(0, 15)
                  .map((skill) => (
                    <Badge
                      key={skill.id}
                      variant="outline"
                      className={cn(
                        "rounded-full px-3 py-1.5 transition-all border-slate-200",
                        skills.length < 10
                          ? "cursor-pointer hover:bg-[#4669ed]/10 hover:border-[#4669ed]"
                          : "opacity-50 cursor-not-allowed"
                      )}
                      onClick={() => skills.length < 10 && onAddSkill(skill.name)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {skill.name}
                    </Badge>
                  ))}
              </div>
            )}
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex gap-2">
            {isEditing && onResetSkills && (
              <Button
                variant="outline"
                onClick={onResetSkills}
                disabled={isSaving}
                className="rounded-[48px]"
              >
                Cancel
              </Button>
            )}
            {hasSkills && (
              <Button
                onClick={onSave}
                disabled={isSaving}
                className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Save Skills
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Badge
              key={skill}
              className="bg-[#d1e0fc] text-[#2b42a3] rounded-[48px] px-3 py-1.5 text-xs font-semibold"
            >
              {skill}
            </Badge>
          ))}
        </div>
      )}

      {/* Add Skill Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add a Skill</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="skill-input" className="text-sm font-medium text-slate-700 mb-2 block">
              Skill Name
            </Label>
            <Input
              id="skill-input"
              value={customSkill}
              onChange={(e) => {
                setCustomSkill(e.target.value);
                setInputError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSkill();
                }
              }}
              placeholder="e.g., Machine Learning, Python, TensorFlow"
              className={cn(
                "rounded-lg",
                inputError && "border-red-500 focus-visible:ring-red-500"
              )}
              maxLength={100}
              autoFocus
            />
            {inputError && (
              <p className="text-sm text-red-500 mt-1">{inputError}</p>
            )}
            <p className="text-xs text-slate-500 mt-2">
              {skills.length}/10 skills added
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDialogClose}
              className="rounded-[48px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddCustomSkill}
              disabled={skills.length >= 10}
              className="rounded-[48px] text-white"
              style={{
                background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Skill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
